import { useEffect } from "react";
import {
  logUserProperty,
  selectAccountSettingsLoaded,
  useAuthStore,
} from "@um/ui";

/** GA user property carrying the pilot flag. */
export const DENTAL_PILOT_USER_PROPERTY = "dentalPilot";

/**
 * Mirrors the pilot flag to a GA user property.
 *
 * Every other cohort marker in this app is mirrored this way — RoutineMenuVariant,
 * routinePaywallExperiment, FeedbackStrategy*, the account attributes — because
 * the team's funnels are read "split by user property". An enrolment EVENT
 * alone is not enough: an event-based segment only covers the exploration's
 * date range, whereas a user property rides on every later event, which is
 * what lets "pilot vs not" be sliced on create-character, routine-selected,
 * paywall-shown and so on without touching them.
 *
 * Keyed on the loaded settings rather than on the /explore click that set the
 * flag, deliberately: user properties are per GA client and are not
 * retroactive, so stamping only at enrolment would leave a participant's
 * second device — the app they were told to download — unmarked. Reading the
 * settings doc covers every device, sign-back-in, and the dev toggle alike.
 *
 * Waits for the settings to be loaded for the CURRENT account so an
 * account switch cannot briefly report the previous account's value.
 */
export function useDentalPilotUserProperty(): void {
  const loaded = useAuthStore(selectAccountSettingsLoaded);
  const dentalPilot = useAuthStore(
    (s) => s.accountSettings?.dentalPilot === true
  );

  useEffect(() => {
    if (!loaded) return;
    logUserProperty(DENTAL_PILOT_USER_PROPERTY, dentalPilot ? "true" : "false");
  }, [loaded, dentalPilot]);
}
